import * as Sentry from "@sentry/browser"
import {
  hasResolvedDocumentValue,
  useDocument,
} from "@shared/components/providers/SupergrainProvider"
import { isUnifiedCardStack, readonlyUnifiedCopy } from "@shared/lib/card-stack/unified-migration"
import { calculateCourseDateCombined } from "@shared/lib/course-date-combined"
import type { CardStack, UnifiedCardStack } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { CourseDateCombined } from "@shared/models/course-date-combined"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { applyTemplateToLessonIfNeeded, usePhantomLessonDeps } from "react-app/lib/phantom-lesson"
import { reportFollowedLoadError } from "./followed-course-errors"

export type FollowedLesson =
  | { status: "none" }
  | { status: "loading" }
  | { status: "error" }
  | { status: "loaded"; lesson: UnifiedCardStack }

/**
 * The followed class's lesson on `day`, as its own planner shows it: a lesson saved before its
 * cards were edited is filled from the day's template, and one from the old editor is migrated.
 */
export function useFollowedLesson(
  followedCourse: Course,
  planbook: Planbook,
  rotationCalendar: RotationCalendar,
  day: string | null,
): { courseDate: CourseDateCombined | null; lesson: FollowedLesson } {
  const courseDate =
    day === null
      ? null
      : calculateCourseDateCombined(day, followedCourse, planbook, rotationCalendar, () => null)
  const cardStackId = courseDate?.cardStackId ?? null
  const templateId = cardStackId === null ? null : (courseDate?.cardStackTemplateId ?? null)

  const deps = usePhantomLessonDeps()
  const templateHandle = useDocument("card-stack", templateId)
  const template = hasResolvedDocumentValue(templateHandle) ? templateHandle.value : null
  const lessonHandle = useDocument("card-stack", cardStackId)
  if (cardStackId !== null && templateHandle.status !== "pending") {
    applyTemplateToLessonIfNeeded(deps, cardStackId, templateId, template)
  }

  if (cardStackId === null) return { courseDate, lesson: { status: "none" } }
  if (lessonHandle.status === "error") {
    reportFollowedLoadError(lessonHandle.error)
    return { courseDate, lesson: { status: "error" } }
  }
  if (!hasResolvedDocumentValue(lessonHandle)) return { courseDate, lesson: { status: "loading" } }

  const value = lessonHandle.value
  if (isUnifiedCardStack(value)) {
    const isFillingFromTemplate =
      templateHandle.status === "pending" && value.attributes.proseMirror === null
    return {
      courseDate,
      lesson: isFillingFromTemplate ? { status: "loading" } : { status: "loaded", lesson: value },
    }
  }
  try {
    return { courseDate, lesson: { status: "loaded", lesson: migratedCopy(value) } }
  } catch (error) {
    reportFollowedLoadError(error)
    return { courseDate, lesson: { status: "error" } }
  }
}

const migratedCopies = new WeakMap<object, UnifiedCardStack>()

/** One copy per stored document, so a preview of it isn't rebuilt on every render. */
function migratedCopy(lesson: CardStack): UnifiedCardStack {
  let copy = migratedCopies.get(lesson)
  if (copy === undefined) {
    copy = readonlyUnifiedCopy(lesson, (error) => Sentry.captureException(error))
    migratedCopies.set(lesson, copy)
  }
  return copy
}
